/*
 * @Author: Teemo Ding
 * @Date: 2021-06-09 14:15:14
 * @LastEditors: Teemo Ding
 * @LastEditTime: 2021-08-05 16:16:13
 * @FilePath: /unifyxp-v2-store-portal/src/pages/SalesChannelProductsConfig/SalesChannelProductAlias/ListPage/slice/index.ts
 */

import {
  createSlice,
  createSelector,
  createAsyncThunk,
} from '@reduxjs/toolkit';
import { message } from 'antd';
import { EnumCollapseId } from '../common';
import {
  assData,
  defaultStatus,
  generateDefaultState as _generateDefaultState,
  hasError,
} from 'src/pages/SalesChannelProductsConfig/slice/reduxCommonFunctions';

import {
  EnumReducers,
  reducers,
  getTarget,
  getCollapseOpenStatus as _getCollapseOpenStatus,
  getCollapseValidationMsgDisplay as _getCollapseValidationMsgDisplay,
  getGeneralFeaturedProduct as _getGeneralFeaturedProduct,
  getHasModified as _getHasModified,
  getSalesChannel as _getSalesChannel,
  getSalesChannelName as _getSalesChannelName,
  getState as _getState,
  getPageEnabled as _getPageEnabled,
} from 'src/pages/SalesChannelProductsConfig/slice/selectors';

import {
  convertDataFromServerToRedux,
  duplicateConvertDataFromServerToRedux,
} from '../tools';

import {
  request_ProductContent,
  request_DeleteAlias,
  request_DuplicateAliasProductContent,
} from '@/src/request/salesChannel/product/content';

export { EnumCollapseId };

const fixedCollapse = [
  EnumCollapseId.seoSetting,
  EnumCollapseId.longCanvas,
  EnumCollapseId.contentDetails,
  EnumCollapseId.carousel,
  EnumCollapseId.thumbnailImage,
];
const _defaultState = _generateDefaultState(fixedCollapse);
const defaultState = {
  uuid: '',
  loaded: false,
  enabled: false,
  alias: [],
  type: '',
  show: null,
  ..._defaultState,
  ...defaultStatus,
};
const _currentReducer = reducers(EnumReducers.productAlias);

export const initAliasList = createAsyncThunk(
  'initSalesChannelAliasList',
  async ({ body, params }: any) => {
    return await request_ProductContent(body, params);
  },
);

export const duplicateAlias = createAsyncThunk(
  'duplicateAlias',
  async (body: any) => {
    const response = await request_DuplicateAliasProductContent(body);
    return await response;
  },
);

export const deleteAlias = createAsyncThunk(
  'deleteAlias',
  async (body: any) => {
    const response = await request_DeleteAlias(body);
    return await response;
  },
);

const SalesChannelProductAlias = createSlice({
  name: 'SalesChannelProductAlias',
  initialState: defaultState,
  reducers: {
    onAliasDuplicate(state, action) {
      const { name } = action.payload;
      state.hasModified = true;
      state.alias.push({
        carousel: state.carousel,
        longCanvas: state.longCanvas,
        thumbnailImage: state.thumbnailImage,
        contentDetails: {
          ...state.contentDetails,
          productName: name,
        },
        seoSetting: state.seoSetting,
        ..._defaultState,
      });
    },

    onDeleteAlias(state, action) {
      state.hasModified = true;
      state.alias.splice(action.payload, 1);
    },

    onErrorChange(state, action) {
      const { id, status } = action.payload;
      state.collapseErrorMsgDisplay[id].hasError = status;
    },
    validation(state) {
      if (state.enabled) {
        hasError(state, state.collapseErrorMsgDisplay || {});
      } else {
        state.isDataValid = true;
      }
    },
    resetData(state) {
      assData(state, defaultState);
    },
  },
  extraReducers: (builder) => {
    builder.addCase(initAliasList.fulfilled, (state, action) => {
      const response = action.payload;
      const _data = convertDataFromServerToRedux(response) || {};
      assData(state, _data);
    });
    builder.addCase(duplicateAlias.fulfilled, (state, action) => {
      const response = action.payload;
      const _data = duplicateConvertDataFromServerToRedux(response) || {};
      message.success('duplicate successfully');
      const aliasData = state.alias;
      aliasData.push(_data);
      assData(state);
    });
    builder.addCase(deleteAlias.fulfilled, (state, action) => {
      const response = action.payload;
      const _id = response.uuid || {};
      message.success('delete successfully');
      const aliasData = state.alias;
      const aliasDataFilter = aliasData.filter((item) => item.uuid !== _id);
      assData(state, { alias: aliasDataFilter });
    });
  },
});

export const getPageEnabled = () => {
  return _getPageEnabled(EnumReducers.productAlias);
};

export const getAllAlias = () => {
  return createSelector(
    (state: any) => {
      const stateData = getTarget(state, _currentReducer);
      return stateData.alias;
    },
    (alias) => {
      return alias;
    },
  );
};

export const getContent = (id) => {
  return createSelector(
    (state: any) => {
      const stateData = getTarget(state, _currentReducer);
      return stateData[id];
    },
    (content) => {
      return content;
    },
  );
};

export const getCollapseOpenStatus = (id) => {
  return _getCollapseOpenStatus(EnumReducers.productAlias, id);
};

export const getCollapseValidationMsgDisplay = (id) => {
  return _getCollapseValidationMsgDisplay(EnumReducers.productAlias, id);
};

export const getGeneralFeaturedProduct = () => {
  return _getGeneralFeaturedProduct(EnumReducers.productAlias);
};

export const getState = () => {
  return _getState(EnumReducers.productAlias);
};

export const getHasModified = () => {
  return _getHasModified(EnumReducers.productAlias);
};

export const getSalesChannel = () => {
  return _getSalesChannel(EnumReducers.productAlias);
};

export const getSalesChannelName = () => {
  return _getSalesChannelName(EnumReducers.productAlias);
};

export const {
  onErrorChange,
  onAliasDuplicate,
  onDeleteAlias,
  validation,
  resetData,
} = SalesChannelProductAlias.actions;

export default SalesChannelProductAlias.reducer;
